import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { deriveProbabilityIndex, deriveSeverityIndex, matrixClassification } from "@/lib/aepContent";

const SHORT = {
  "Demandas e carga de trabalho": "Demandas",
  "Controle, autonomia e clareza": "Controle",
  "Liderança e apoio da supervisão": "Liderança",
  "Relações interpessoais e clima": "Relações",
  "Reconhecimento, justiça e segurança psicológica": "Reconhecimento",
  "Condições do ambiente físico e recursos de trabalho": "Ambiente",
  "Saúde mental, bem-estar e sinais de sofrimento": "Saúde mental",
  "Assédio, violência e condutas inadequadas": "Assédio",
  "Organização da produção e períodos de maior demanda": "Produção",
};

function cellClass(level) {
  return level === 0 ? "risk-0" : `risk-${level}`;
}

/**
 * Cross-company heatmap: aggregates all assessments whose company CNPJ matches.
 * Allows the SST consultant to see one consolidated view when a single CNPJ has
 * been duplicated as multiple company records, OR when the same legal entity
 * has multiple GHE/setores spread across several assessments.
 */
export default function CnpjConsolidated() {
  const { cnpj } = useParams();
  const [groupBy, setGroupBy] = useState("company"); // company | ghe | individual | geral
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [dims, setDims] = useState([]);

  useEffect(() => {
    let mounted = true;
    api.get(`/cnpj/${cnpj}/consolidated`, { params: { group_by: groupBy } })
      .then((r) => {
        if (!mounted) return;
        // === Unificar classificação Dashboard ↔ Matriz AIHA ===
        // O backend devolve `overall.label` derivado só do score médio (ex.:
        // 2.80 = "Moderado"), mas a Matriz AIHA usa P×S (que sobe pra "Alto"
        // quando há respostas críticas). Reclassificamos aqui pra manter o
        // KPI "Classificação" e a tabela de grupos alinhados com a matriz
        // (fonte única de verdade — mesma lógica do CnpjAEPReport).
        const reclass = (agg) => {
          if (!agg || !agg.dimensions || (agg.response_count || 0) === 0) return agg;
          const dimsWithData = agg.dimensions.filter((d) => d.responses > 0);
          if (!dimsWithData.length) return agg;
          const worstScore = Math.max(...dimsWithData.map((d) => d.score), 0);
          const p = deriveProbabilityIndex(agg.overall_score);
          const s = deriveSeverityIndex(worstScore, agg.critical_count || 0, agg.response_count, agg.overall_score, false);
          if (!p || !s) return agg;
          const label = matrixClassification(p, s);
          const levelMap = { "Baixo": 1, "Moderado": 2, "Alto": 3, "Crítico": 4 };
          const keyMap = { "Baixo": "low", "Moderado": "moderate", "Alto": "high", "Crítico": "critical" };
          return {
            ...agg,
            overall: {
              ...(agg.overall || {}),
              label,
              level: levelMap[label] || (agg.overall?.level ?? 0),
              key: keyMap[label] || agg.overall?.key,
              matrix_derived: true,
            },
          };
        };
        const patched = {
          ...r.data,
          overall: reclass(r.data.overall),
          groups: (r.data.groups || []).map(reclass),
        };
        setData(patched);
        setDims(patched.dimensions || []);
      })
      .catch((e) => { if (mounted) setErr(e.response?.data?.detail || e.message); });
    return () => { mounted = false; };
  }, [cnpj, groupBy]);

  if (err) {
    return (
      <div className="p-12 text-center">
        <i className="fa-solid fa-circle-exclamation text-5xl text-rose-500 mb-4"></i>
        <h1 className="font-display text-2xl font-extrabold">{err}</h1>
        <Link to="/app/empresas" className="btn btn-secondary mt-6">Voltar</Link>
      </div>
    );
  }
  if (!data) return <div className="p-12 text-slate-500 text-center"><i className="fa-solid fa-circle-notch fa-spin mr-2"></i> Carregando…</div>;

  const canonical = data.canonical || {};

  return (
    <div className="p-8 lg:p-12 max-w-7xl mx-auto" data-testid="cnpj-consolidated-page">
      <div className="mb-8 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <Link to="/app/empresas" className="text-xs text-slate-500 hover:text-emerald-900 mb-2 inline-block">
            <i className="fa-solid fa-arrow-left mr-1"></i> Empresas
          </Link>
          <div className="overline mb-2">Consolidado por CNPJ</div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight text-slate-900">{canonical.razao_social || "—"}</h1>
          <div className="flex flex-wrap gap-3 text-xs text-slate-500 mt-2 font-mono">
            <span>CNPJ: <strong>{data.cnpj_formatted || data.cnpj}</strong></span>
            <span>·</span>
            <span>{data.company_count} cadastro(s)</span>
            <span>·</span>
            <span>{data.assessment_count || 0} avaliação(ões)</span>
          </div>
        </div>
        <Link
          to={`/aep/cnpj/${cnpj}`}
          className="btn btn-primary self-start lg:self-end"
          data-testid="cnpj-aep-generate"
        >
          <i className="fa-solid fa-file-pdf"></i> Gerar AEP Consolidado
        </Link>
      </div>

      {/* Overall KPIs */}
      {data.overall && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <Kpi label="Score consolidado" value={(data.overall.overall_score || 0).toFixed(2)} testid="cnpj-kpi-score" />
          <Kpi label="Classificação" value={data.overall.overall?.label || "—"} testid="cnpj-kpi-class" />
          <Kpi label="Respostas" value={data.overall.response_count || 0} testid="cnpj-kpi-responses" />
          <Kpi label="Críticas (≥4,5)" value={data.overall.critical_count || 0} testid="cnpj-kpi-critical" />
        </div>
      )}

      {/* Group-by selector */}
      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <span className="overline">Agrupar por:</span>
        {[
          ["geral", "Geral"],
          ["company", "Por cadastro"],
          ["ghe", "Por GHE/Setor"],
          ["individual", "Por avaliação"],
        ].map(([k, label]) => (
          <button key={k} onClick={() => setGroupBy(k)}
                  data-testid={`cnpj-group-${k}`}
                  className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider border transition-colors ${
                    groupBy === k ? "bg-emerald-900 text-white border-emerald-900"
                                  : "bg-white text-slate-600 border-slate-300 hover:border-slate-500"}`}>
            {label}
          </button>
        ))}
      </div>

      {/* Heatmap table */}
      <div className="card overflow-x-auto" data-testid="cnpj-heatmap">
        {data.groups.length === 0 || data.groups.every((g) => (g.response_count || 0) === 0) ? (
          <div className="text-slate-500 py-12 text-center text-sm">
            <i className="fa-solid fa-clipboard-list text-3xl mb-3 text-slate-300 block"></i>
            Nenhuma resposta consolidada para este agrupamento.
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-left text-slate-500 border-b border-slate-200">
              <tr>
                <th className="py-3 font-semibold uppercase">Grupo</th>
                <th className="font-semibold uppercase text-center">Avaliações</th>
                <th className="font-semibold uppercase text-center">Respostas</th>
                <th className="font-semibold uppercase text-center">Score</th>
                <th className="font-semibold uppercase text-center">Classe</th>
                {dims.map((d) => (
                  <th key={d} className="font-semibold uppercase text-center px-1" title={d}>
                    {SHORT[d] || d.slice(0, 8)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.groups.map((g) => (
                <tr key={g.key} className="border-b border-slate-100">
                  <td className="py-2.5 font-semibold text-slate-900">{g.label}</td>
                  <td className="text-center font-mono">{g.assessment_count}</td>
                  <td className="text-center font-mono">{g.response_count}</td>
                  <td className="text-center font-mono font-bold">{(g.overall_score || 0).toFixed(2)}</td>
                  <td className="text-center">
                    <span className={`${cellClass(g.overall?.level || 0)} border px-2 py-0.5 font-bold uppercase tracking-wider`}>
                      {g.overall?.label || "—"}
                    </span>
                  </td>
                  {dims.map((d) => {
                    const dim = g.dimensions?.find((x) => x.dimensao === d);
                    const lvl = dim?.classification?.level || 0;
                    return (
                      <td key={d} className="text-center px-1">
                        <span className={`${cellClass(lvl)} inline-block w-full py-1 font-mono`}>
                          {dim?.score ? dim.score.toFixed(1) : "—"}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* List of company records sharing this CNPJ */}
      {data.companies.length > 1 && (
        <div className="card mt-6" data-testid="cnpj-company-list">
          <div className="overline mb-3">
            Cadastros com este CNPJ ({data.companies.length})
          </div>
          <p className="text-xs text-slate-500 mb-3">
            Este CNPJ aparece em múltiplos cadastros. Considere unificar caso sejam a mesma empresa.
          </p>
          <ul className="space-y-2">
            {data.companies.map((c) => (
              <li key={c.id} className="flex justify-between items-center text-sm">
                <div>
                  <span className="font-semibold text-slate-900">{c.razao_social}</span>
                  {c.nome_fantasia && <span className="text-slate-500 ml-2">· {c.nome_fantasia}</span>}
                </div>
                <Link to={`/app/empresas/${c.id}/mapa`} className="text-xs text-emerald-900 hover:underline">
                  Mapa individual <i className="fa-solid fa-arrow-right"></i>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, testid }) {
  return (
    <div className="card" data-testid={testid}>
      <div className="overline">{label}</div>
      <div className="kpi-number text-4xl text-slate-900 mt-2">{value}</div>
    </div>
  );
}
